"use client";

import React, { useState } from "react";
import { FileSystemItem } from "@/types/fileManager";
import { Folder, FileText, Image as ImageIcon, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { generateImageSrc } from "@/utils/helper";
import { useTheme } from "@/contexts/ThemeContext";

interface FileItemProps {
  item: FileSystemItem;
  isSelected: boolean;
  selectionMode: "single" | "multiple";
  showCheckbox: boolean;
  onSelect: (item: FileSystemItem, multiSelect?: boolean) => void;
  onDoubleClick: (item: FileSystemItem) => void;
  onDelete?: (item: FileSystemItem) => void;
  allowDelete?: boolean;
}

export const FileItem: React.FC<FileItemProps> = ({
  item,
  isSelected,
  selectionMode,
  showCheckbox,
  onSelect,
  onDoubleClick,
  onDelete,
  allowDelete = false,
}) => {
  const { isDark } = useTheme();
  const [showDeleteButton, setShowDeleteButton] = useState(false);

  const getFileIcon = () => {
    const extension = item.name.split(".").pop()?.toLowerCase();
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"];

    if (extension && imageExtensions.includes(extension)) {
      // Generate image URL using the helper function with the item's path
      const imageUrl = generateImageSrc(item.path);

      if (imageUrl) {
        return (
          <div
            className={`w-full h-full rounded-lg overflow-hidden border ${isDark ? "border-auth-background" : "border-gray-200"}`}
          >
            <img
              src={imageUrl}
              alt={item.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                const nextElement =
                  e.currentTarget.parentElement?.querySelector(
                    ".fallback-icon",
                  );
                if (nextElement) {
                  nextElement.classList.remove("hidden");
                }
              }}
            />
            <div
              className={`fallback-icon hidden w-full h-full flex items-center justify-center ${isDark ? "bg-auth-background" : "bg-gray-50"}`}
            >
              <ImageIcon className="w-8 h-8 text-green-500" />
            </div>
          </div>
        );
      }

      // Fallback to icon if URL generation fails
      return (
        <div
          className={`w-full h-full flex items-center justify-center rounded-lg border ${isDark ? "bg-auth-background border-auth-background" : "bg-gray-50 border-gray-200"}`}
        >
          <ImageIcon className="w-8 h-8 text-green-500" />
        </div>
      );
    }

    return (
      <div
        className={`w-full h-full flex items-center justify-center rounded-lg border ${isDark ? "bg-auth-background border-auth-background" : "bg-gray-50 border-gray-200"}`}
      >
        <FileText
          className={`w-8 h-8 ${isDark ? "text-gray-300" : "text-gray-500"}`}
        />
      </div>
    );
  };

  const formatFileSize = (size?: number) => {
    if (!size && size !== 0) return "Size unknown";

    const units = ["B", "KB", "MB", "GB"];
    let unitIndex = 0;
    let fileSize = size;

    while (fileSize >= 1024 && unitIndex < units.length - 1) {
      fileSize /= 1024;
      unitIndex++;
    }

    return `${fileSize.toFixed(1)} ${units[unitIndex]}`;
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const multiSelect =
      selectionMode === "multiple" && (e.ctrlKey || e.metaKey || e.shiftKey);
    onSelect(item, multiSelect);
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDoubleClick(item);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === "Enter") {
        onDoubleClick(item);
      } else {
        const multiSelect =
          selectionMode === "multiple" &&
          (e.ctrlKey || e.metaKey || e.shiftKey);
        onSelect(item, multiSelect);
      }
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(item);
    }
  };

  return (
    <div className="flex flex-col">
      {/* Card with image/icon */}
      <div
        className={`
          relative rounded-lg border cursor-pointer transition-colors aspect-square overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-300
          ${isDark ? "hover:bg-auth-border" : "hover:bg-gray-50"}
          ${
            isSelected
              ? `${isDark ? "bg-blue-900/30 border-blue-500 ring-2 ring-blue-500/50" : "bg-blue-50 border-blue-300 ring-2 ring-blue-200"}`
              : `${isDark ? "border-auth-background" : "border-gray-200"}`
          }
        `}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setShowDeleteButton(true)}
        onMouseLeave={() => setShowDeleteButton(false)}
        tabIndex={0}
        role="button"
        aria-label={`${item.isFolder ? "Folder" : "File"}: ${item.name}${isSelected ? " (selected)" : ""}`}
      >
        {/* Full-size image or icon */}
        {item.isFolder ? (
          <div className={`w-full h-full flex items-center justify-center ${isDark ? "bg-blue-900/30" : "bg-blue-50"}`}>
            <Folder className="w-8 h-8 text-blue-500" />
          </div>
        ) : (
          getFileIcon()
        )}

        {allowDelete && showDeleteButton && !item.isFolder && (
          <Button
            variant="ghost"
            size="sm"
            className={`absolute top-2 right-2 h-6 w-6 p-0 text-red-500 hover:text-red-700 z-10 rounded ${
              isDark
                ? "bg-auth-card/80 hover:bg-red-900/50"
                : "bg-white/80 hover:bg-red-50"
            }`}
            onClick={handleDelete}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* File information outside the card */}
      <div className="mt-2 text-center px-1">
        <div className="flex items-center gap-2 mb-1">
          {/* Checkbox on the left side of filename */}
          {showCheckbox && (
            <div className="flex-shrink-0">
              <Checkbox
                checked={isSelected}
                onChange={() => onSelect(item, selectionMode === "multiple")}
                className="w-4 h-4"
              />
            </div>
          )}
          <div
            className={`font-medium text-xs truncate min-w-0 flex-1 ${
              isSelected
                ? `${isDark ? "text-blue-400" : "text-blue-700"}`
                : `${isDark ? "text-gray-100" : "text-gray-900"}`
            }`}
            title={item.name}
          >
            {item.name}
          </div>
        </div>
        <div
          className={`text-xs ${isDark ? "text-gray-300" : "text-gray-500"}`}
        >
          {item.isFolder ? "Folder" : formatFileSize(item.size)}
        </div>
      </div>
    </div>
  );
};
